﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { createUseStyles } from "react-jss";
import type { IntlContext } from "~base/Internationalization/IntlContext";
import { useIntl } from "~base/Internationalization/IntlContext";
import type { SoPNLConsilioTheme } from "~jrad/Styling/SoPNLConsilioTheme";
import type { SelfServiceViewAction } from "../Store";
import type { SelfServiceViewState } from "../Types";
import SelfServicePageNavigationControls from "../ChildComponents/PageNavigationControls";

import storeImageGooglePlay from "~jrad-resources/Images/google-play-badge.svg?url";
import storeImageAppStore from "~jrad-resources/Images/apple-appstore-badge.svg?url";
import { useSettings } from "~base/Settings/SettingsContext";
import QRCode from "~base/Components/Primitives/QRCode";
import { isAndroid, isIOS } from "react-device-detect";
import {
    DataGatheringStatus,
    usePostSelfServiceRegistrationStatus,
} from "~jrad/Services/API/Account/PostSelfServiceRegistrationAppStatus";
import type { PostSelfServiceRegistrationRequest } from "~jrad/Services/API/Account/PostSelfServiceRegistration";
import type { ApiStatus } from "~base/Services/API";
import { getErrorMessage } from "~base/Services/API";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

export interface SelfServicePageAppRegistrationProps {
    isLoading: boolean;
    state: SelfServiceViewState;
    dispatch: React.Dispatch<SelfServiceViewAction>;
    postRegistrationRequest: () => void;
    currentRequestData: PostSelfServiceRegistrationRequest;
}

const useStyles = createUseStyles((theme: SoPNLConsilioTheme) => ({
    container: {
        display: "flex",
        justifyContent: "space-evenly",
        marginBottom: "18px",
    },
    qrCode: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    downloadInfo: {},
    downloadStores: {
        maxWidth: "600px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-around",
        flexWrap: "wrap",
    },
    downloadStoreLink: {
        marginBottom: "2rem",
        marginLeft: "1rem",
        marginRight: "1rem",
    },
    downloadStoreImage: {
        height: "4rem",
        width: "auto",
    },
    downloadLinkInfo: {
        fontFamily: theme.constants.defaultFont,
        fontSize: "16px",
        textAlign: "center",
    },
    downloadLink: {
        display: "inline-block",

        marginTop: "5px",

        fontFamily: "monospace",
        fontSize: "17px",
        fontWeight: 500,

        color: "#009fdf",

        "&:hover": {
            color: "#002d72",
        },
    },
}));

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const PageAppRegistration: React.FunctionComponent<
    SelfServicePageAppRegistrationProps
> = ({
    isLoading,
    state,
    dispatch,
    postRegistrationRequest,
    currentRequestData,
}) => {
    const intl = useIntl();
    const classes = useStyles();

    const settings = useSettings();

    const publicBaseUrl = location.protocol + "//" + location.host;
    const fascoQrData = state.responseData.AppRegistrationAppUrl;

    const appDownloadLink =
        publicBaseUrl + settings.valueAsString("DownloadUppAppPath");

    const hasDetectedDevice = isIOS || isAndroid;

    const [loadingVerificationStatus] = usePostSelfServiceRegistrationStatus({
        data: currentRequestData,
        pollInterval: 5000,
        call:
            state.verificationStatus !== DataGatheringStatus.Error &&
            state.verificationStatus !== DataGatheringStatus.Success,
        callback: async (
            status,
            response,
            responseData,
            requestData,
            context,
            traceContext
        ) => {
            await callbackWithErrorHandling(
                intl,
                dispatch,
                status,
                response,
                async () => {
                    dispatch({
                        type: "setVerificationStatus",
                        status: responseData.Status,
                    });

                    if (responseData.Status == DataGatheringStatus.Error) {
                        dispatch({
                            type: "fetchError",
                            message: intl.formatText(
                                "VisiFinance.UI.SelfServiceRegister.Verification.Status.Error"
                            ),
                        });
                    }
                }
            );
        },
    });

    return (
        <>
            <div className={classes.container}>
                <div className={classes.qrCode}>
                    <QRCode
                        testIdentifier="AppRegistrationQrCode"
                        size={150}
                        value={fascoQrData}
                        errorCorrectionLevel="L"
                    />
                </div>
                <div className={classes.downloadInfo}>
                    <div className={classes.downloadStores}>
                        {(isAndroid || !hasDetectedDevice) && (
                            <a
                                href={settings.valueAsString(
                                    "DataGatheringAppPlayStoreUrl"
                                )}
                                target="_blank"
                                className={classes.downloadStoreLink}
                            >
                                <img
                                    src={storeImageGooglePlay}
                                    className={classes.downloadStoreImage}
                                />
                            </a>
                        )}
                        {(isIOS || !hasDetectedDevice) && (
                            <a
                                href={settings.valueAsString(
                                    "DataGatheringAppAppleAppStoreUrl"
                                )}
                                target="_blank"
                                className={classes.downloadStoreLink}
                            >
                                <img
                                    src={storeImageAppStore}
                                    className={classes.downloadStoreImage}
                                />
                            </a>
                        )}
                    </div>
                    <div className={classes.downloadLinkInfo}>
                        <div>
                            {intl.formatText(
                                "VisiFinance.UI.SelfServiceRegister.Verification.LinkToFindApp"
                            )}
                        </div>
                        <a
                            href={appDownloadLink}
                            target="_blank"
                            className={classes.downloadLink}
                        >
                            {appDownloadLink}
                        </a>
                    </div>
                </div>
            </div>
            <SelfServicePageNavigationControls
                state={state}
                dispatch={dispatch}
                postRegistrationRequest={postRegistrationRequest}
                disablePrev={isLoading}
                disableNext
            />
        </>
    );
};

const callbackWithErrorHandling = async (
    intl: IntlContext,
    dispatch: React.Dispatch<SelfServiceViewAction>,
    status: ApiStatus,
    response: Response,
    successAction: () => Promise<void>
) => {
    if (status == "Success") {
        await successAction();
    } else {
        dispatch({
            type: "fetchError",
            message: await getErrorMessage(intl, response),
        });
    }
};

export default PageAppRegistration;
